import React from 'react';
import './styles.css';
import { RSButton } from 'reactsymbols-kit';
import { Row, Col } from '../Grid';
import { RSFormLabel } from 'reactsymbols-kit';
import MerlinSelect, { type MerlinSelectOption } from '../MerlinSelect';
import { observer } from 'mobx-react';
import { TYPE_INFO, TYPES } from '../../stores/model/metamodel/mpc_config';
import Path from './Path';
import { type MPCInspectorUI } from './MultiplePropagationConstraintsInspector';
import {
  type EditedConstraint,
  type TemporaryPath,
  type PathsByObjectType,
  type ConstraintKey,
} from './mpcModels';
import Styles from '../../styles/colors';
import type EditObjectController from '../../merode/controllers/EditObjectController';

// Convert the info about the types to options for the select component
const TYPE_OPTIONS = Object.values(TYPE_INFO).map((t) => ({
  value: t.key,
  label: t.symbol,
}));

/**
 * Utility function to check whether a constraint is (syntactically) valid
 */
function isValidConstraint(constraint: EditedConstraint) {
  const { type } = constraint;
  return (
    TYPES.includes(type as string) &&
    constraint.constrainedObjectName != null &&
    constraint.primaryPath != null &&
    constraint.secondaryPath != null
  );
}

// Custom render function for a path as an option in the select component.
// MerlinSelect forwards the bare option (a `TemporaryPath` here, since this
// select's options ARE path objects); cast at the boundary.
const PathOption = (option: MerlinSelectOption) => (
  <Path path={option as unknown as TemporaryPath} inSelect />
);
// Custom render function for a path as the value in the select component.
const PathValue = (option: MerlinSelectOption) => {
  const p = option as unknown as TemporaryPath;
  return `${p.elements[0].previousObject.name} > ${p.elements.map((e) => e.nextObject.name).join(' > ')}`;
};

/**
 * (Controlled) from for editing a multiple propagation constraint
 *
 * When not adding, only the type can be changed.
 *
 * Props:
 * - onRequestClose: callback to close the modal
 * - onRequestCancel: callback to cancel editing
 * - onChange: change handler
 * - adding: is the edited MPC a new MCP?
 * - objectTypeName: the name of the objectType that the MPC belongs to
 * - constraint: the edited constraint.
 *     - type: the type of the constraint (== or !=)
 *     - primaryPath
 *     - secondaryPath
 *     - constrainedObjectName: the name of the object that is constrained
 * - paths: the list of possible paths
 */
/** The change keys this form drives. */
type MPCFormChangeKey = Exclude<ConstraintKey, 'id'>;

interface EditMultiplePropagationConstraintFormProps {
  /** Callback to cancel editing (only set when adding a new MPC). */
  onRequestCancel?: () => void;
  /**
   * Callback to close the modal. The parent forwards the inspector's
   * `onStopEdit(id)`; the form invokes it with no argument (the id is ignored),
   * so the optional id keeps the call sites type-compatible.
   */
  onRequestClose?: (id?: string) => void;
  /** Change handler. Arguments: key, value. */
  onChange: (key: MPCFormChangeKey, value: string | undefined) => void;
  /** Is the edited MPC a new one? */
  adding: boolean;
  /** The name of the objectType that the MPC belongs to. */
  objectTypeName: string;
  /** The edited constraint. */
  constraint: EditedConstraint;
  /** The list of possible paths, keyed by constrained-object name. */
  paths: PathsByObjectType;
  /** The MPCInspector ui store (forwarded by the parent for parity). */
  ui?: MPCInspectorUI;
  /** The edit controller (forwarded by the parent for parity). */
  controller?: EditObjectController;
}

const EditMultiplePropagationConstraintForm = ({
  onRequestCancel,
  onRequestClose,
  onChange,
  adding,
  objectTypeName,
  constraint,
  paths,
}: EditMultiplePropagationConstraintFormProps) => {
  const valid = isValidConstraint(constraint);

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (valid && onRequestClose != null) {
      onRequestClose();
    }
  };

  const relevantPaths = paths[constraint.constrainedObjectName!];
  const pathOptions = adding ? relevantPaths || [] : undefined;

  const primaryPathId = constraint.primaryPath != null ? constraint.primaryPath.id : undefined;
  const secondaryPathId =
    constraint.secondaryPath != null ? constraint.secondaryPath.id : undefined;

  // Change handler for the primary path. Also clear the secondary path when it
  // is selected as primary path.
  const onChangePrimaryPath = (option: MerlinSelectOption | null) => {
    if (option == null) {
      return;
    }
    const p = option as unknown as TemporaryPath;
    if (p === constraint.secondaryPath) {
      onChange('secondaryPath', undefined);
    }
    onChange('primaryPath', p.id);
  };

  return (
    <div className="multiple-propagation-constraint-editor mpc-path">
      <div className="title inspector-subtitle">{adding ? 'Add ' : 'Edit '} constraint</div>
      <form className="form w-full" onSubmit={onSubmit}>
        <div className="rs-element rs-input-container rs-input mpc-holder">
          <RSFormLabel>Object holding the constraint: </RSFormLabel>
          <div className="readonly-value">{objectTypeName}</div>
        </div>
        <div className="rs-element rs-input-container rs-input mpc-constrained-object">
          <RSFormLabel>Object to constrain: </RSFormLabel>
          {adding ? (
            <MerlinSelect
              name="constrainedObject"
              options={Object.keys(paths).map((v) => ({ value: v, label: v }))}
              value={constraint.constrainedObjectName}
              onChange={(option: MerlinSelectOption | null) =>
                option != null && onChange('constrainedObjectName', option.value as string)
              }
              clearable={false}
              searchable={false}
            />
          ) : (
            <div className="readonly-value">{constraint.constrainedObjectName}</div>
          )}
        </div>
        <div className="rs-element rs-input-container rs-input mpc-type">
          <RSFormLabel>Type: </RSFormLabel>
          <MerlinSelect
            name="type"
            options={TYPE_OPTIONS}
            value={constraint.type}
            onChange={(option: MerlinSelectOption | null) =>
              option != null && onChange('type', option.value as string)
            }
            clearable={false}
            searchable={false}
          />
        </div>
        <div className="rs-element rs-input-container rs-input primary-path">
          <RSFormLabel>Primary path: </RSFormLabel>
          {adding ? (
            <MerlinSelect
              name="primaryPath"
              // The path selects use opaque `TemporaryPath` options rendered by
              // PathOption/PathValue (not `{label,value}` shaped); cast at the
              // boundary, exactly as the v1 `valueKey='id'` + renderers did.
              options={pathOptions as unknown as MerlinSelectOption[] | undefined}
              optionRenderer={PathOption}
              valueRenderer={PathValue}
              value={primaryPathId}
              valueKey={'id'}
              onChange={onChangePrimaryPath}
              clearable={false}
              searchable={false}
            />
          ) : (
            <div className="readonly-path">
              <Path path={constraint.primaryPath} />
            </div>
          )}
        </div>
        <div className="rs-element rs-input-container rs-input secondary-path">
          <RSFormLabel>Secondary path: </RSFormLabel>
          {adding ? (
            <MerlinSelect
              name="secondaryPath"
              options={
                pathOptions!.filter(
                  (o) => o !== constraint.primaryPath
                ) as unknown as MerlinSelectOption[]
              }
              optionRenderer={PathOption}
              valueRenderer={PathValue}
              value={secondaryPathId}
              valueKey={'id'}
              onChange={(option: MerlinSelectOption | null) =>
                onChange(
                  'secondaryPath',
                  option != null ? (option as unknown as TemporaryPath).id : undefined
                )
              }
              clearable={false}
              searchable={false}
            />
          ) : (
            <div className="readonly-path">
              <Path path={constraint.secondaryPath} />
            </div>
          )}
        </div>

        <div className="button-wrapper mt-2.5 w-full">
          <Row gutter={5}>
            {onRequestCancel != null && (
              <Col md={6}>
                <RSButton
                  background={Styles.defaultGradient}
                  color={Styles.darkTextColor}
                  value="Cancel"
                  type="button"
                  onClick={onRequestCancel}
                />
              </Col>
            )}
            <Col md={onRequestCancel != null ? 6 : 12}>
              <RSButton
                type="submit"
                background={Styles.blueGradient}
                value={adding ? 'Add' : 'Close'}
                disabled={!valid}
              />
            </Col>
          </Row>
        </div>
      </form>
    </div>
  );
};

// Wrap the view in observer
const ObservedEditMultiplePropagationConstraintForm = observer(EditMultiplePropagationConstraintForm);
export default ObservedEditMultiplePropagationConstraintForm;
